import { DatePipe } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CaseExtraction, DocumentBatch } from './document-ocr.models';
import { DocumentOcrService } from './document-ocr.service';
import { MATCH_TIER_LABEL, matchTier } from './match-tier';

type RowState = 'pending' | 'failed' | 'needs_review' | 'resolved' | 'rejected';

interface Row {
  key: string;
  batch: DocumentBatch;
  extraction?: CaseExtraction;
  state: RowState;
  title: string;
  subtitle: string;
  updatedAt: string;
}

const STATE_LABEL: Record<RowState, string> = {
  pending: 'Processing',
  failed: 'Failed',
  needs_review: 'Needs review',
  /** Extraction has been matched/linked to a grievance case — not the grievance's own workflow status. */
  resolved: 'Case created',
  rejected: 'Rejected',
};

/** Extraction (Gemini call + matching) typically takes a few seconds — poll, don't leave a stale "Processing" row. */
const POLL_INTERVAL_MS = 4000;

@Component({
  selector: 'app-ocr-documents',
  standalone: true,
  imports: [FormsModule, RouterLink, DatePipe],
  templateUrl: './ocr-documents.component.html',
  styleUrl: './ocr-documents.component.scss',
})
export class OcrDocumentsComponent implements OnInit {
  private readonly ocr = inject(DocumentOcrService);
  private readonly destroyRef = inject(DestroyRef);
  private pollHandle: ReturnType<typeof setInterval> | null = null;

  readonly loading = this.ocr.loading;
  readonly error = this.ocr.error;
  readonly searchQuery = signal('');
  readonly stateFilter = signal<RowState | 'all'>('all');
  readonly stateLabel = STATE_LABEL;
  readonly matchTier = matchTier;
  readonly matchTierLabel = MATCH_TIER_LABEL;

  private readonly rows = computed<Row[]>(() => {
    const out: Row[] = [];
    for (const item of this.ocr.batches()) {
      const { batch, extractions } = item;
      if (!extractions.length) {
        out.push({
          key: `batch-${batch.id}`,
          batch,
          state: batch.status === 'failed' ? 'failed' : 'pending',
          title: `Batch #${batch.id}`,
          subtitle: batch.status === 'failed' ? (batch.errorMessage || 'Failed') : `Status: ${batch.status}`,
          updatedAt: batch.createdAt,
        });
        continue;
      }
      for (const extraction of extractions) {
        const ext = extraction.extractedJson;
        let state: RowState = 'needs_review';
        if (extraction.matchStatus === 'rejected') state = 'rejected';
        else if (extraction.grievanceRef != null) state = 'resolved';
        out.push({
          key: `extraction-${extraction.id}`,
          batch,
          extraction,
          state,
          title: ext?.applicantName || `Case #${extraction.id}`,
          subtitle: ext?.subjectEnglish || ext?.subjectHindi || '',
          updatedAt: extraction.updatedAt,
        });
      }
    }
    return out.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  });

  readonly counts = computed(() => {
    const c: Record<RowState, number> = { pending: 0, failed: 0, needs_review: 0, resolved: 0, rejected: 0 };
    for (const r of this.rows()) c[r.state] += 1;
    return c;
  });

  readonly filtered = computed(() => {
    const state = this.stateFilter();
    const q = this.searchQuery().trim().toLowerCase();
    return this.rows().filter((r) => {
      if (state !== 'all' && r.state !== state) return false;
      if (q) {
        const hay = `${r.title} ${r.subtitle} ${r.batch.id}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  });

  ngOnInit(): void {
    void this.ocr.refresh();
    this.pollHandle = setInterval(() => {
      if (this.counts().pending > 0) void this.ocr.refresh();
    }, POLL_INTERVAL_MS);
    this.destroyRef.onDestroy(() => {
      if (this.pollHandle) clearInterval(this.pollHandle);
    });
  }

  setState(s: RowState | 'all'): void {
    this.stateFilter.set(this.stateFilter() === s ? 'all' : s);
  }

  refresh(): void {
    void this.ocr.refresh();
  }
}
